import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import { api } from '../../lib/api';

const MODULE_LABELS = {
  case: 'Case study',
  drill: 'Calculation exercise',
  'spot-error': 'Spot-the-error',
  outpatient: 'Outpatient prescription',
  cpd: 'CPD entry',
};

const CPD_TYPE_LABELS = {
  article: 'Article / reading',
  course: 'Course / training',
  audit: 'Audit',
  intervention: 'Clinical intervention',
  other: 'Other',
};

const ALL_MODULE_TYPES = ['case', 'drill', 'spot-error', 'outpatient', 'cpd'];

const formatDate = (value) => {
  if (!value) return '';
  try {
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return new Date(`${value}T00:00:00`).toLocaleDateString('en-GB');
    }
    return new Date(value).toLocaleDateString('en-GB');
  } catch {
    return String(value);
  }
};

const formatDateTime = (value) => {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString('en-GB');
  } catch {
    return String(value);
  }
};

const entryDateMs = (entry) => {
  const v = entry.occurredAt;
  if (!v) return 0;
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) {
    return new Date(`${v}T00:00:00`).getTime();
  }
  return new Date(v).getTime();
};

const readQueryParams = () => {
  const params = new URLSearchParams(window.location.search);
  const rawTypes = params.get('type');
  const types = rawTypes
    ? rawTypes.split(',').map((s) => s.trim()).filter((s) => ALL_MODULE_TYPES.includes(s))
    : [];
  return {
    portfolioId: params.get('id') || '',
    from: params.get('from') || '',
    to: params.get('to') || '',
    types,
    framework: params.get('framework') || '',
  };
};

const StudentPortfolioPrintView = ({ token, user }) => {
  const [entries, setEntries] = useState([]);
  const [enabledFrameworks, setEnabledFrameworks] = useState([]);
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const filters = useMemo(readQueryParams, []);

  const load = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      const tasks = [api.getPortfolio(token)];
      if (filters.portfolioId) tasks.push(api.getPortfolioById(filters.portfolioId, token));
      const [portfolioFeed, portfolioDetail] = await Promise.all(tasks);
      setEntries(Array.isArray(portfolioFeed.entries) ? portfolioFeed.entries : []);
      setEnabledFrameworks(Array.isArray(portfolioFeed.enabledFrameworks) ? portfolioFeed.enabledFrameworks : []);
      setPortfolio(portfolioDetail?.portfolio || null);
      setError('');
    } catch (err) {
      setError(err?.message || 'Failed to load portfolio.');
    } finally {
      setLoading(false);
    }
  }, [token, filters.portfolioId]);

  useEffect(() => { load(); }, [load]);

  const frameworkSlugForTags = filters.framework || null;
  const frameworkLabel = useMemo(() => {
    if (!frameworkSlugForTags) return '';
    const match = enabledFrameworks.find(
      (f) => f.slug === frameworkSlugForTags || f.id === frameworkSlugForTags
    );
    return match?.name || frameworkSlugForTags;
  }, [enabledFrameworks, frameworkSlugForTags]);

  const visible = useMemo(() => {
    let rows = entries.slice();
    if (filters.portfolioId) {
      rows = rows.filter((e) => Array.isArray(e.portfolioIds) && e.portfolioIds.includes(filters.portfolioId));
    }
    if (filters.types.length > 0) {
      const set = new Set(filters.types);
      rows = rows.filter((e) => set.has(e.entryKind || e.moduleType));
    }
    if (filters.from || filters.to) {
      const fromMs = filters.from ? new Date(`${filters.from}T00:00:00`).getTime() : -Infinity;
      const toMs = filters.to ? new Date(`${filters.to}T23:59:59`).getTime() : Infinity;
      rows = rows.filter((e) => {
        const ts = entryDateMs(e);
        if (!ts) return false;
        return ts >= fromMs && ts <= toMs;
      });
    }
    rows.sort((a, b) => entryDateMs(b) - entryDateMs(a));
    return rows;
  }, [entries, filters]);

  const summary = useMemo(() => {
    let cpdHours = 0;
    let reflected = 0;
    const byType = {};
    for (const e of visible) {
      const t = e.entryKind || e.moduleType;
      byType[t] = (byType[t] || 0) + 1;
      if (e.kind === 'cpd') cpdHours += Number(e.hours) || 0;
      if (e.reflection?.body && e.reflection.body.trim()) reflected += 1;
    }
    return { cpdHours, reflected, byType };
  }, [visible]);

  const filterTagsForEntry = (entry) => {
    const tags = Array.isArray(entry.tags) ? entry.tags : [];
    if (!frameworkSlugForTags) return tags;
    return tags.filter((t) => t.frameworkSlug === frameworkSlugForTags || t.frameworkId === frameworkSlugForTags);
  };

  const filterChips = useMemo(() => {
    const chips = [];
    if (filters.from) chips.push(`From ${formatDate(filters.from)}`);
    if (filters.to) chips.push(`To ${formatDate(filters.to)}`);
    if (filters.types.length > 0) {
      chips.push(`Types: ${filters.types.map((t) => MODULE_LABELS[t] || t).join(', ')}`);
    }
    if (frameworkLabel) chips.push(`Framework: ${frameworkLabel}`);
    return chips;
  }, [filters, frameworkLabel]);

  const generatedAt = useMemo(() => new Date().toLocaleString('en-GB'), []);

  const handlePrint = () => {
    if (typeof window !== 'undefined') window.print();
  };

  const handleClose = () => {
    if (typeof window !== 'undefined') {
      window.location.href = '/';
    }
  };

  return (
    <div className="portfolio-print">
      <style>{`
        .portfolio-print {
          background: #f6f7fb;
          min-height: 100vh;
          padding: 24px 0;
        }
        .portfolio-print__sheet {
          max-width: 820px;
          margin: 0 auto;
          background: white;
          padding: 32px 40px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          color: #212529;
          font-family: -apple-system, "Segoe UI", Roboto, sans-serif;
          font-size: 13px;
          line-height: 1.4;
        }
        .portfolio-print__actions {
          max-width: 820px;
          margin: 0 auto 12px;
          display: flex;
          gap: 8px;
          justify-content: flex-end;
        }
        .portfolio-print__header {
          border-bottom: 2px solid #212529;
          padding-bottom: 12px;
          margin-bottom: 20px;
        }
        .portfolio-print__header h1 {
          font-size: 22px;
          margin: 0 0 4px 0;
        }
        .portfolio-print__meta {
          font-size: 11px;
          color: #555;
        }
        .portfolio-print__filter-chips {
          margin-top: 6px;
          font-size: 11px;
          color: #444;
        }
        .portfolio-print__filter-chips span {
          display: inline-block;
          background: #eef1f6;
          padding: 2px 8px;
          margin-right: 4px;
          margin-bottom: 4px;
          border-radius: 10px;
        }
        .portfolio-print__summary {
          display: flex;
          gap: 24px;
          margin-bottom: 24px;
          flex-wrap: wrap;
          border-bottom: 1px solid #ccc;
          padding-bottom: 16px;
        }
        .portfolio-print__summary-item {
          min-width: 110px;
        }
        .portfolio-print__summary-label {
          font-size: 10px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: #666;
        }
        .portfolio-print__summary-value {
          font-size: 20px;
          font-weight: 600;
        }
        .portfolio-print__module-counts span {
          display: inline-block;
          background: #f1f3f5;
          padding: 1px 6px;
          margin-right: 4px;
          margin-bottom: 2px;
          border-radius: 8px;
          font-size: 10px;
        }
        .portfolio-print__entries-heading {
          font-size: 15px;
          font-weight: 600;
          margin-bottom: 12px;
          border-bottom: 1px solid #ccc;
          padding-bottom: 4px;
        }
        .portfolio-print__entry {
          page-break-inside: avoid;
          break-inside: avoid;
          margin-bottom: 16px;
          padding: 10px 0;
          border-bottom: 1px solid #e4e6eb;
        }
        .portfolio-print__entry:last-child {
          border-bottom: none;
        }
        .portfolio-print__entry-head {
          display: flex;
          justify-content: space-between;
          align-items: baseline;
          gap: 12px;
          margin-bottom: 4px;
        }
        .portfolio-print__entry-title {
          font-weight: 600;
          font-size: 14px;
          flex: 1;
        }
        .portfolio-print__entry-kind {
          font-size: 10px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: #444;
          background: #eef1f6;
          padding: 2px 8px;
          border-radius: 10px;
          white-space: nowrap;
        }
        .portfolio-print__entry-date {
          font-size: 11px;
          color: #666;
          white-space: nowrap;
        }
        .portfolio-print__entry-meta {
          font-size: 11px;
          color: #555;
          margin-bottom: 4px;
        }
        .portfolio-print__tags {
          margin: 4px 0 6px;
        }
        .portfolio-print__tags span {
          display: inline-block;
          background: #e7f1ff;
          color: #084298;
          padding: 1px 6px;
          margin-right: 4px;
          margin-bottom: 2px;
          border-radius: 8px;
          font-size: 10px;
        }
        .portfolio-print__notes {
          margin-top: 6px;
          font-size: 12px;
          white-space: pre-wrap;
        }
        .portfolio-print__notes-label {
          font-weight: 600;
          font-size: 11px;
          color: #555;
          margin-top: 6px;
        }
        @media print {
          .portfolio-print { background: white; padding: 0; }
          .portfolio-print__sheet { box-shadow: none; max-width: none; padding: 0; }
          .portfolio-print__actions { display: none !important; }
          @page { size: A4; margin: 1.5cm; }
        }
      `}</style>

      <div className="portfolio-print__actions">
        <Button variant="primary" onClick={handlePrint} disabled={loading}>
          <i className="bi bi-printer me-1" /> Print / Save as PDF
        </Button>
        <Button variant="outline-secondary" onClick={handleClose}>Close</Button>
      </div>

      <div className="portfolio-print__sheet">
        <div className="portfolio-print__header">
          <h1>{portfolio?.name || 'Learning portfolio'}</h1>
          {portfolio?.description ? (
            <div className="portfolio-print__meta" style={{ marginBottom: 4 }}>{portfolio.description}</div>
          ) : null}
          <div className="portfolio-print__meta">
            {user?.displayName ? <>{user.displayName} &middot; </> : null}
            {user?.email ? <>{user.email} &middot; </> : null}
            Generated {generatedAt}
          </div>
          {filterChips.length > 0 ? (
            <div className="portfolio-print__filter-chips">
              {filterChips.map((c) => <span key={c}>{c}</span>)}
            </div>
          ) : null}
        </div>

        {error ? <Alert variant="danger">{error}</Alert> : null}

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spinner animation="border" />
          </div>
        ) : (
          <>
            <div className="portfolio-print__summary">
              <div className="portfolio-print__summary-item">
                <div className="portfolio-print__summary-label">Entries</div>
                <div className="portfolio-print__summary-value">{visible.length}</div>
              </div>
              <div className="portfolio-print__summary-item">
                <div className="portfolio-print__summary-label">CPD hours</div>
                <div className="portfolio-print__summary-value">{summary.cpdHours.toFixed(1)}</div>
              </div>
              <div className="portfolio-print__summary-item">
                <div className="portfolio-print__summary-label">With reflection</div>
                <div className="portfolio-print__summary-value">{summary.reflected}</div>
              </div>
              <div className="portfolio-print__summary-item" style={{ flex: 1, minWidth: 220 }}>
                <div className="portfolio-print__summary-label">By module</div>
                <div className="portfolio-print__module-counts">
                  {Object.entries(summary.byType).length === 0 ? (
                    <span>—</span>
                  ) : (
                    Object.entries(summary.byType)
                      .sort((a, b) => b[1] - a[1])
                      .map(([type, count]) => (
                        <span key={type}>{MODULE_LABELS[type] || type}: {count}</span>
                      ))
                  )}
                </div>
              </div>
            </div>

            <div className="portfolio-print__entries-heading">Entries</div>
            {visible.length === 0 ? (
              <div style={{ fontSize: 12, color: '#666' }}>
                No entries match the selected filters.
              </div>
            ) : (
              visible.map((e) => {
                const moduleKind = e.entryKind || e.moduleType;
                const kindLabel = MODULE_LABELS[moduleKind] || moduleKind;
                const tags = filterTagsForEntry(e);
                return (
                  <div className="portfolio-print__entry" key={`${moduleKind}:${e.entryId}`}>
                    <div className="portfolio-print__entry-head">
                      <div className="portfolio-print__entry-title">{e.sourceTitle || '(untitled)'}</div>
                      <span className="portfolio-print__entry-kind">{kindLabel}</span>
                      <span className="portfolio-print__entry-date">{formatDate(e.occurredAt)}</span>
                    </div>
                    <div className="portfolio-print__entry-meta">
                      {e.kind === 'cpd' ? (
                        <>
                          {CPD_TYPE_LABELS[e.entryType] || e.entryType}
                          {e.hours ? <> &middot; {Number(e.hours).toFixed(Number.isInteger(Number(e.hours)) ? 0 : 1)} hours</> : null}
                        </>
                      ) : null}
                      {e.kind === 'session' && e.effectiveScore != null ? (
                        <>
                          Score: {Math.round(e.effectiveScore)}%
                          {e.scoreOverride != null ? ' (lecturer-adjusted)' : ''}
                        </>
                      ) : null}
                    </div>
                    {tags.length > 0 ? (
                      <div className="portfolio-print__tags">
                        {tags.map((t) => (
                          <span key={t.outcomeItemId}>{t.code} {t.label}</span>
                        ))}
                      </div>
                    ) : null}
                    {e.kind === 'cpd' && e.cpdReflection ? (
                      <>
                        <div className="portfolio-print__notes-label">CPD notes</div>
                        <div className="portfolio-print__notes">{e.cpdReflection}</div>
                      </>
                    ) : null}
                    {e.kind === 'session' && e.reviewComment ? (
                      <>
                        <div className="portfolio-print__notes-label">Auto-grading review</div>
                        <div className="portfolio-print__notes">{e.reviewComment}</div>
                      </>
                    ) : null}
                    {e.reflection?.body ? (
                      <>
                        <div className="portfolio-print__notes-label">
                          Reflection
                          {e.reflection.updatedAt ? (
                            <span style={{ fontWeight: 400, color: '#888' }}>
                              {' '}— {formatDateTime(e.reflection.updatedAt)}
                            </span>
                          ) : null}
                        </div>
                        <div className="portfolio-print__notes">{e.reflection.body}</div>
                      </>
                    ) : null}
                    {Array.isArray(e.lecturerComments) && e.lecturerComments.length > 0 ? (
                      <>
                        <div className="portfolio-print__notes-label">Lecturer feedback</div>
                        {e.lecturerComments.map((c) => (
                          <div key={c.id} className="portfolio-print__notes" style={{ marginTop: 8 }}>
                            <strong>{c.lecturerName || 'Lecturer'}</strong>
                            <span style={{ color: '#888', fontWeight: 400 }}>
                              {' '}— {formatDateTime(c.updatedAt)}
                            </span>
                            <div>{c.body}</div>
                          </div>
                        ))}
                      </>
                    ) : null}
                  </div>
                );
              })
            )}

          </>
        )}
      </div>
    </div>
  );
};

export default StudentPortfolioPrintView;
